import type { AttachmentDto } from '@tms/shared';
import { ArrowDown, ArrowUp, ImagePlus, Plus, Trash2, X } from 'lucide-react';
import { useId, useRef, useState } from 'react';
import type { FieldErrors } from 'react-hook-form';
import { toast } from 'sonner';
import { RichTextEditor } from '../../components/rich-text';
import { Button, ImageZoom } from '../../components/ui';
import { useErrorText, useT } from '../../i18n';
import {
  imageFilesFrom,
  isWithinSizeLimit,
  uploadAttachment,
} from '../attachments/api';

export interface StepValue {
  action: string;
  expectedResult?: string | null;
  /** Optional screenshot; kept as the uploaded attachment so the thumbnail can render. */
  screenshot?: AttachmentDto | null;
}

export interface StepsEditorProps {
  projectCode: string;
  steps: StepValue[];
  errors?: FieldErrors;
  onChange: (steps: StepValue[]) => void;
}

const emptyStep: StepValue = { action: '', expectedResult: '', screenshot: null };

/** Screenshot slot of a single step: pick, preview as a thumbnail, remove. */
const StepScreenshot = ({
  projectCode,
  stepNumber,
  screenshot,
  onChange,
}: {
  projectCode: string;
  stepNumber: number;
  screenshot: AttachmentDto | null | undefined;
  onChange: (screenshot: AttachmentDto | null) => void;
}) => {
  const { t } = useT();
  const { errorText } = useErrorText();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (files: File[]): Promise<void> => {
    const file = files[0];
    if (!file) return;
    if (!isWithinSizeLimit(file)) {
      toast.error(t('richText.imageTooLarge'));
      return;
    }
    setUploading(true);
    try {
      onChange(await uploadAttachment(projectCode, file));
    } catch (error) {
      toast.error(errorText(error, 'richText.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[12px] font-medium text-ink">{t('cases.stepScreenshot')}</span>

      {/* See RichTextEditor: a visually hidden input stays in flow at zero
          size so it cannot extend the page. */}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        tabIndex={-1}
        aria-hidden="true"
        className="size-0 overflow-hidden border-0 p-0"
        onChange={(event) => {
          void upload(imageFilesFrom(event.target.files));
          event.target.value = '';
        }}
      />

      {screenshot ? (
        <div className="flex items-start gap-2">
          {/* A thumbnail, never the full image: a screenshot of any size keeps
              the step row the same height, and opens full size on click. */}
          <ImageZoom src={screenshot.url} alt={screenshot.fileName} size="sm" />
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onChange(null)}
            aria-label={t('cases.removeStepScreenshot', { number: stepNumber })}
            className="text-fail hover:text-fail"
          >
            <X className="size-3.5" />
          </Button>
        </div>
      ) : (
        <Button
          size="sm"
          onClick={() => inputRef.current?.click()}
          loading={uploading}
          aria-label={t('cases.addStepScreenshot', { number: stepNumber })}
        >
          {uploading ? null : <ImagePlus className="size-3.5" />}
          {t('cases.addScreenshot')}
        </Button>
      )}
    </div>
  );
};

/** Ordered step editor: add, edit, reorder and remove steps of a test case. */
export const StepsEditor = ({ projectCode, steps, errors, onChange }: StepsEditorProps) => {
  const { t } = useT();
  const fieldId = useId();

  const update = (index: number, patch: Partial<StepValue>) => {
    onChange(steps.map((step, position) => (position === index ? { ...step, ...patch } : step)));
  };

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= steps.length) return;
    const next = [...steps];
    const [moved] = next.splice(index, 1);
    if (moved) next.splice(target, 0, moved);
    onChange(next);
  };

  const stepError = (index: number): string | undefined => {
    const list = errors?.steps as unknown as Array<{ action?: { message?: string } }> | undefined;
    return list?.[index]?.action?.message;
  };

  return (
    <div className="flex flex-col gap-3">
      {steps.length === 0 ? (
        <p className="rounded-md border border-dashed border-border px-3 py-4 text-center text-[13px] text-ink-subtle">
          {t('cases.stepsEmpty')}
        </p>
      ) : null}

      {steps.map((step, index) => (
        <div key={index} className="rounded-lg border border-border bg-canvas p-3">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-[12px] font-semibold uppercase tracking-wide text-ink-subtle">
              {t('cases.step', { number: index + 1 })}
            </p>
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => move(index, -1)}
                disabled={index === 0}
                aria-label={t('cases.moveStepUp', { number: index + 1 })}
              >
                <ArrowUp className="size-3.5" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => move(index, 1)}
                disabled={index === steps.length - 1}
                aria-label={t('cases.moveStepDown', { number: index + 1 })}
              >
                <ArrowDown className="size-3.5" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onChange(steps.filter((_, position) => position !== index))}
                aria-label={t('cases.removeStep', { number: index + 1 })}
                className="text-fail hover:text-fail"
              >
                <Trash2 className="size-3.5" />
              </Button>
            </div>
          </div>

          <div className="grid gap-3 lg:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor={`${fieldId}-action-${index}`}
                className="text-[12px] font-medium text-ink"
              >
                {t('cases.stepAction')}
              </label>
              <RichTextEditor
                id={`${fieldId}-action-${index}`}
                projectCode={projectCode}
                value={step.action}
                invalid={Boolean(stepError(index))}
                onChange={(value) => update(index, { action: value })}
                placeholder={t('cases.stepActionPlaceholder')}
                rows={3}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor={`${fieldId}-expected-${index}`}
                className="text-[12px] font-medium text-ink"
              >
                {t('cases.stepExpected')}
              </label>
              <RichTextEditor
                id={`${fieldId}-expected-${index}`}
                projectCode={projectCode}
                value={step.expectedResult ?? ''}
                onChange={(value) => update(index, { expectedResult: value })}
                placeholder={t('cases.stepExpectedPlaceholder')}
                rows={3}
              />
            </div>
          </div>

          <div className="mt-3">
            <StepScreenshot
              projectCode={projectCode}
              stepNumber={index + 1}
              screenshot={step.screenshot}
              onChange={(screenshot) => update(index, { screenshot })}
            />
          </div>

          {stepError(index) ? (
            <p className="mt-1.5 text-[12px] text-fail" role="alert">
              {stepError(index)}
            </p>
          ) : null}
        </div>
      ))}

      <div>
        <Button onClick={() => onChange([...steps, { ...emptyStep }])}>
          <Plus className="size-3.5" />
          {t('cases.addStep')}
        </Button>
      </div>
    </div>
  );
};
